import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

const VEHICLE_EMOJI = {
  bike: '🚲',
  boda: '🏍️',
  tuktuk: '🛺',
  car: '🚗',
};

const VEHICLE_LABEL = {
  bike: 'Bike',
  boda: 'Boda Boda',
  tuktuk: 'Tuk Tuk',
  car: 'Car',
};

const PAYMENT_ICON = {
  cash: '💵',
  mpesa: '📱',
  card: '💳',
  wallet: '👛',
};

const FavoriteRoutes = ({ onRebook, currentPickup, currentDropoff, currentVehicle, currentService, currentPayment }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [saveError, setSaveError] = useState('');
  const [userId, setUserId] = useState(null);

  const canSaveCurrent = currentPickup && currentDropoff &&
    currentPickup !== 'Locating you...' && currentDropoff;

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          setUserId(user?.id);
          await fetchFavorites(user?.id);
        } else {
          setLoading(false);
        }
      } catch {
        setLoading(false);
      }
    };
    init();
  }, []);

  const fetchFavorites = async (uid) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('favorite_routes')
        ?.select('*')
        ?.eq('user_id', uid)
        ?.order('last_used_at', { ascending: false, nullsFirst: false })
        ?.order('created_at', { ascending: false })
        ?.limit(5);

      if (error) throw error;
      setFavorites(data || []);
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFavorite = async () => {
    if (!saveLabel?.trim()) {
      setSaveError('Please enter a name for this route');
      return;
    }
    if (!canSaveCurrent) {
      setSaveError('Please fill in pickup and drop-off locations first');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const { error } = await supabase
        ?.from('favorite_routes')
        ?.insert({
          user_id: userId,
          label: saveLabel?.trim(),
          pickup_address: currentPickup,
          dropoff_address: currentDropoff,
          vehicle_type: currentVehicle || 'boda',
          payment_method: currentPayment || 'cash',
          service_type: currentService || 'ride',
        });

      if (error) throw error;
      setSaveLabel('');
      setShowSaveForm(false);
      await fetchFavorites(userId);
    } catch (err) {
      console.error('Failed to save favorite:', err);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      const { error } = await supabase
        ?.from('favorite_routes')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      setFavorites(prev => prev?.filter(f => f?.id !== id));
    } catch (err) {
      console.error('Failed to delete favorite:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleRebook = async (fav) => {
    // Increment use_count and update last_used_at
    try {
      await supabase
        ?.from('favorite_routes')
        ?.update({
          use_count: (fav?.use_count || 0) + 1,
          last_used_at: new Date()?.toISOString(),
        })
        ?.eq('id', fav?.id);
    } catch {
      // non-critical, ignore
    }

    onRebook?.({
      pickup: fav?.pickup_address,
      dropoff: fav?.dropoff_address,
      vehicle: fav?.vehicle_type,
      service: fav?.service_type,
      payment: fav?.payment_method,
    });
  };

  if (!userId) return null;

  return (
    <div className="bg-white rounded-2xl border border-[#0a1628]/10 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#ffd700]/15 flex items-center justify-center">
            <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-[#ffd700]">
              <path d="M8 1.5l1.6 3.2 3.5.5-2.55 2.48.6 3.5L8 9.5l-3.15 1.68.6-3.5L2.9 5.2l3.5-.5z" />
            </svg>
          </div>
          <h3 className="text-sm font-bold text-[#0a1628]">Quick Rebook</h3>
          {favorites?.length > 0 && (
            <span className="text-[10px] font-bold text-[#0a1628]/40 bg-[#0a1628]/6 px-1.5 py-0.5 rounded-full">
              {favorites?.length}
            </span>
          )}
        </div>
        {canSaveCurrent && !showSaveForm && (
          <button
            onClick={() => setShowSaveForm(true)}
            className="flex items-center gap-1 text-xs font-semibold text-[#ffd700] bg-[#ffd700]/10 hover:bg-[#ffd700]/20 px-2.5 py-1.5 rounded-lg transition-colors"
          >
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3">
              <path d="M7 2v10M2 7h10" strokeLinecap="round" />
            </svg>
            Save Route
          </button>
        )}
      </div>
      {/* Save Form */}
      {showSaveForm && (
        <div className="mx-4 mb-3 p-3 bg-[#ffd700]/8 rounded-xl border border-[#ffd700]/20">
          <p className="text-xs font-semibold text-[#0a1628]/60 mb-2">Name this route</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={saveLabel}
              onChange={e => setSaveLabel(e?.target?.value)}
              onKeyDown={e => e?.key === 'Enter' && handleSaveFavorite()}
              placeholder="e.g. Home to Office"
              maxLength={40}
              className="flex-1 text-sm px-3 py-2 bg-white border border-[#0a1628]/15 rounded-lg focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 text-[#0a1628] placeholder-[#0a1628]/35 transition-colors"
              autoFocus
            />
            <button
              onClick={handleSaveFavorite}
              disabled={saving}
              className="px-3 py-2 bg-[#0a1628] text-[#ffd700] text-xs font-bold rounded-lg hover:bg-[#1a365d] transition-colors disabled:opacity-50"
            >
              {saving ? '...' : 'Save'}
            </button>
            <button
              onClick={() => { setShowSaveForm(false); setSaveLabel(''); setSaveError(''); }}
              className="px-2.5 py-2 bg-[#0a1628]/8 text-[#0a1628]/50 text-xs font-semibold rounded-lg hover:bg-[#0a1628]/12 transition-colors"
            >
              ✕
            </button>
          </div>
          {/* Route preview */}
          <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[#0a1628]/50">
            <span className="truncate max-w-[120px]">{currentPickup}</span>
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-2.5 h-2.5 flex-shrink-0">
              <path d="M2 6h8M7 3l3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="truncate max-w-[120px]">{currentDropoff}</span>
            <span className="flex-shrink-0 ml-1">{VEHICLE_EMOJI?.[currentVehicle] || '🏍️'}</span>
          </div>
          {saveError && (
            <p className="text-[10px] text-red-500 mt-1.5">{saveError}</p>
          )}
        </div>
      )}
      {/* Favorites List */}
      {loading ? (
        <div className="px-4 pb-4 space-y-2">
          {[1, 2]?.map(i => (
            <div key={i} className="h-16 rounded-xl bg-[#0a1628]/5 animate-pulse" />
          ))}
        </div>
      ) : favorites?.length === 0 ? (
        <div className="px-4 pb-4 text-center py-4">
          <div className="w-10 h-10 rounded-full bg-[#ffd700]/10 flex items-center justify-center mx-auto mb-2">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5 text-[#ffd700]">
              <path d="M10 2l2.4 4.8 5.3.77-3.85 3.75.91 5.3L10 14.1l-4.76 2.52.91-5.3L2.3 7.57l5.3-.77z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="text-xs font-semibold text-[#0a1628]/50">No saved routes yet</p>
          <p className="text-[10px] text-[#0a1628]/35 mt-0.5">
            {canSaveCurrent ? 'Tap "Save Route" to save your current trip' : 'Enter locations above to save a route'}
          </p>
        </div>
      ) : (
        <div className="px-4 pb-4 space-y-2">
          {favorites?.map(fav => (
            <div
              key={fav?.id}
              className="group flex items-center gap-3 p-3 rounded-xl bg-[#0a1628]/4 hover:bg-[#0a1628]/7 border border-transparent hover:border-[#0a1628]/8 transition-all duration-150 cursor-pointer"
              onClick={() => handleRebook(fav)}
            >
              {/* Vehicle icon */}
              <div className="w-9 h-9 rounded-xl bg-white border border-[#0a1628]/8 flex items-center justify-center flex-shrink-0 shadow-sm">
                <span className="text-lg leading-none">{VEHICLE_EMOJI?.[fav?.vehicle_type] || '🏍️'}</span>
              </div>

              {/* Route info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <p className="text-xs font-bold text-[#0a1628] truncate">{fav?.label}</p>
                  {fav?.use_count > 0 && (
                    <span className="text-[9px] font-bold text-[#0a1628]/35 bg-[#0a1628]/6 px-1 py-0.5 rounded-full flex-shrink-0">
                      ×{fav?.use_count}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-[10px] text-[#0a1628]/50">
                  <span className="truncate max-w-[90px]">{fav?.pickup_address}</span>
                  <svg viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-2 h-2 flex-shrink-0">
                    <path d="M1 5h8M6 2l3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="truncate max-w-[90px]">{fav?.dropoff_address}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[9px] text-[#0a1628]/40 font-medium">
                    {VEHICLE_LABEL?.[fav?.vehicle_type] || fav?.vehicle_type}
                  </span>
                  <span className="text-[#0a1628]/20">·</span>
                  <span className="text-[9px] text-[#0a1628]/40">
                    {PAYMENT_ICON?.[fav?.payment_method] || '💵'} {fav?.payment_method}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={e => { e?.stopPropagation(); handleRebook(fav); }}
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-[#0a1628] text-[#ffd700] text-[10px] font-bold rounded-lg hover:bg-[#1a365d] transition-colors opacity-0 group-hover:opacity-100"
                >
                  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-2.5 h-2.5">
                    <path d="M1 6a5 5 0 1010 0" strokeLinecap="round" />
                    <path d="M11 3v3H8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Book
                </button>
                <button
                  onClick={e => { e?.stopPropagation(); handleDelete(fav?.id); }}
                  disabled={deletingId === fav?.id}
                  className="w-6 h-6 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                >
                  {deletingId === fav?.id ? (
                    <div className="w-2.5 h-2.5 border border-red-300 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-2.5 h-2.5">
                      <path d="M2 3h8M5 3V2h2v1M4 3v6h4V3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FavoriteRoutes;
